// TikTok cover/thumbnail URLs served via the feed endpoint (not the single-post one) can come back
// as HEIC, which most non-Safari browsers can't render natively.
// See https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
const isHeicBuffer = (buffer: ArrayBuffer): boolean => {
  const bytes = new Uint8Array(buffer.slice(4, 12));
  const marker = String.fromCharCode(...bytes);

  return (
    marker.includes("ftyp") &&
    (marker.includes("heic") ||
      marker.includes("heix") ||
      marker.includes("mif1"))
  );
};

const DEFAULT_JPEG_QUALITY = 0.8;

// Only downscales when the decoded image is actually larger than maxDimension - these are thumbnails,
// so there's nothing to gain (and some quality to lose) from re-encoding an already-small image.
const resizeIfLarger = async (
  blob: Blob,
  maxDimension: number,
  quality: number,
): Promise<Blob> => {
  try {
    const bitmap = await createImageBitmap(blob);
    const largestSide = Math.max(bitmap.width, bitmap.height);

    if (largestSide <= maxDimension) {
      bitmap.close();

      return blob;
    }

    const scale = maxDimension / largestSide;
    const targetWidth = Math.round(bitmap.width * scale);
    const targetHeight = Math.round(bitmap.height * scale);

    const canvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(targetWidth, targetHeight)
        : Object.assign(document.createElement("canvas"), {
            width: targetWidth,
            height: targetHeight,
          });
    const ctx = canvas.getContext("2d") as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;

    if (!ctx) {
      bitmap.close();

      return blob;
    }

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    const resizedBlob =
      canvas instanceof OffscreenCanvas
        ? await canvas.convertToBlob({ type: "image/jpeg", quality })
        : await new Promise<Blob | null>((resolve) =>
            (canvas as HTMLCanvasElement).toBlob(
              resolve,
              "image/jpeg",
              quality,
            ),
          );

    return resizedBlob ?? blob;
  } catch {
    // Resizing is a bonus, not a requirement - fall back to the already-valid, full-size JPEG.
    return blob;
  }
};

interface ConvertOptions {
  /** JPEG encoding quality, 0-1. Defaults to 0.8 - these are thumbnails, not originals. */
  quality?: number;
  /** Downscale to this longest-side size if the decoded image is bigger, to cut memory/encode cost. */
  maxDimension?: number;
}

// tryConvertHeicToJpeg runs in response to an <img> onError, independently of whatever else is
// happening on the page. A caller that serializes the DOM right after the native error event fires
// (e.g. PDF export) would otherwise capture it mid-conversion - still showing the broken/blob-less
// src. Pending conversions are tracked here so such a caller can await them first.
// See https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
const pendingRecoveries = new Set<Promise<string | null>>();

export const waitForPendingHeicRecoveries = (): Promise<unknown> =>
  Promise.all(pendingRecoveries);

export const tryConvertHeicToJpeg = (
  url: string,
  options: ConvertOptions = {},
): Promise<string | null> => {
  const { quality = DEFAULT_JPEG_QUALITY, maxDimension } = options;

  const promise = (async (): Promise<string | null> => {
    try {
      // cache: "reload" forces a live network request in `cors` mode, bypassing the HTTP cache -
      // otherwise Chrome can intermittently serve back the opaque (no-cors) entry already cached
      // from the plain <img src> load of this same URL, which has no recorded CORS headers and
      // makes this fetch fail with a spurious "No Access-Control-Allow-Origin" error even when the
      // server sends the header correctly. Firefox partitions these differently and never hits it.
      const response = await fetch(url, {
        referrerPolicy: "no-referrer",
        cache: "reload",
      });
      const buffer = await response.arrayBuffer();

      if (!isHeicBuffer(buffer)) {
        return null;
      }

      const heic2any = (await import("heic2any")).default;
      const converted = await heic2any({
        blob: new Blob([buffer]),
        toType: "image/jpeg",
        quality,
      });
      let convertedBlob = Array.isArray(converted) ? converted[0] : converted;

      if (maxDimension) {
        convertedBlob = await resizeIfLarger(
          convertedBlob,
          maxDimension,
          quality,
        );
      }

      return URL.createObjectURL(convertedBlob);
    } catch {
      return null;
    }
  })();

  pendingRecoveries.add(promise);
  promise.finally(() => pendingRecoveries.delete(promise));

  return promise;
};
